import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import axios from "axios";
import { Clock, Users } from "lucide-react";

const Reservations = () => {
  const { id_formation, id_groupe } = useParams();
  const navigate = useNavigate();

  const [initialLoading, setInitialLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formationDetails, setFormationDetails] = useState(null);
  const [groupDetails, setGroupDetails] = useState(null);
  const [waitingList, setWaitingList] = useState([]);
  const [initialError, setInitialError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const [formData, setFormData] = useState({
    nom_etudiant: "",
    prenom_etudiant: "",
    email_etudiant: "",
    numero_tel_etudiant: "",
    commentaire: "",
  });

  const [alert, setAlert] = useState({ type: "", message: "" });

  const fetchDetails = async () => {
    try {
      setInitialLoading(true);
      setInitialError(null);

      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000,
      );

      const formationResponse = await apiClient.get(
        `/formations/${id_formation}`,
        { cancelToken: source.token },
      );
      setFormationDetails(formationResponse.data);

      const groupResponse = await apiClient.get(`/groupes/${id_groupe}`, {
        cancelToken: source.token,
      });
      setGroupDetails(groupResponse.data);

      const waitingListResponse = await apiClient.get(
        `/reservations/groupe/${id_groupe}`,
        { cancelToken: source.token },
      );
      setWaitingList(waitingListResponse.data);

      clearTimeout(timeoutId);
    } catch (err) {
      if (axios.isCancel(err)) {
        setInitialError(
          new Error(
            "Délai d'attente dépassé : Veuillez vérifier votre connexion",
          ),
        );
      } else if (err.response) {
        setInitialError(
          new Error(
            `Erreur du serveur : ${err.response.status} - ${
              err.response.data?.message || "Erreur inconnue"
            }`,
          ),
        );
      } else if (err.request) {
        setInitialError(
          new Error(
            "Erreur réseau : Impossible d'atteindre le serveur. Veuillez vérifier votre connexion.",
          ),
        );
      } else {
        setInitialError(new Error(`Erreur : ${err.message}`));
      }
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id_formation, id_groupe, retryCount]);

  const handleRetry = () => setRetryCount((prev) => prev + 1);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitError) setSubmitError(null);
  };

  const refreshWaitingList = async () => {
    try {
      const waitingListResponse = await apiClient.get(
        `/reservations/groupe/${id_groupe}`,
      );
      setWaitingList(waitingListResponse.data);
    } catch (error) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    setAlert({ type: "", message: "" });

    try {
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        15000,
      );

      // Créer l'étudiant
      const etudiantResponse = await apiClient.post(
        `/etudiants`,
        {
          nom_etudiant: formData.nom_etudiant,
          prenom_etudiant: formData.prenom_etudiant,
          email_etudiant: formData.email_etudiant,
          numero_tel_etudiant: formData.numero_tel_etudiant,
          date_creation: new Date().toISOString(),
          statut_etudiant: "En attente",
        },
        { cancelToken: source.token },
      );

      const newPosition = waitingList.length + 1;

      // Créer la réservation
      await apiClient.post(
        `/reservations`,
        {
          id_formation: parseInt(id_formation),
          id_groupe: parseInt(id_groupe),
          id_etudiant: etudiantResponse.data.id,
          date_reservation: new Date().toISOString(),
          commentaire: formData.commentaire || null,
          position_attente: newPosition,
        },
        { cancelToken: source.token },
      );

      clearTimeout(timeoutId);

      await refreshWaitingList();

      setAlert({
        type: "success",
        message: `Réservation effectuée avec succès ! Votre position dans la liste d'attente : ${newPosition}`,
      });

      setFormData({
        nom_etudiant: "",
        prenom_etudiant: "",
        email_etudiant: "",
        numero_tel_etudiant: "",
        commentaire: "",
      });

      setTimeout(() => navigate("/"), 3000);
    } catch (error) {
      let errorMessage =
        "Une erreur est survenue lors de la réservation. Veuillez réessayer.";
      if (axios.isCancel(error)) {
        errorMessage =
          "Délai d'attente dépassé : Veuillez vérifier votre connexion et réessayer.";
      } else if (error.response) {
        errorMessage = `Erreur du serveur : ${error.response.status} - ${
          error.response.data?.message || "Erreur lors de la réservation"
        }`;
      } else if (error.request) {
        errorMessage =
          "Erreur réseau : Impossible de contacter le serveur. Veuillez vérifier votre connexion.";
      } else {
        errorMessage = `Erreur : ${error.message}`;
      }

      setSubmitError(errorMessage);
      setAlert({ type: "error", message: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRetry = () => {
    const syntheticEvent = { preventDefault: () => {} };
    handleSubmit(syntheticEvent);
  };

  if (initialLoading && retryCount === 0) {
    return (
      <main className="main-area fix">
        <section className="breadcrumb__area breadcrumb__bg">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="breadcrumb__content">
                  <h3 className="title">Liste d'attente</h3>
                  <nav className="breadcrumb">
                    <span>
                      <a href="/">Accueil</a>
                    </span>
                    <span className="breadcrumb-separator">
                      <i className="fas fa-angle-right"></i>
                    </span>
                    <span>Liste d'attente</span>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="singUp-area section-py-120">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-xl-8 col-lg-10 text-center">
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}
                ></div>
                <p className="mt-3">
                  Chargement des détails de la réservation...
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (initialError && (!formationDetails || !groupDetails)) {
    return (
      <main className="main-area fix">
        <section className="breadcrumb__area breadcrumb__bg">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="breadcrumb__content">
                  <h3 className="title">Liste d'attente</h3>
                  <nav className="breadcrumb">
                    <span>
                      <a href="/">Accueil</a>
                    </span>
                    <span className="breadcrumb-separator">
                      <i className="fas fa-angle-right"></i>
                    </span>
                    <span>Liste d'attente</span>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="singUp-area section-py-120">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-xl-8 col-lg-10">
                <div className="alert alert-danger text-center">
                  <i className="fas fa-exclamation-triangle fa-2x mb-3 text-danger"></i>
                  <h4>Erreur de chargement</h4>
                  <p className="mb-3">{initialError.message}</p>
                  <div className="d-flex justify-content-center gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={handleRetry}
                      disabled={initialLoading}
                    >
                      Réessayer
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => navigate("/courses")}
                    >
                      Retour aux cours
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="main-area fix">
      {!initialLoading && (
        <>
          <section className="breadcrumb__area breadcrumb__bg">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <div className="breadcrumb__content">
                    <h3 className="title">Liste d'attente</h3>
                    <nav className="breadcrumb">
                      <span>
                        <a href="/">Accueil</a>
                      </span>
                      <span className="breadcrumb-separator">
                        <i className="fas fa-angle-right"></i>
                      </span>
                      <span>Liste d'attente</span>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="singUp-area section-py-120">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-xl-8 col-lg-10">
                  <div className="singUp-wrap">
                    <h4 className="title">REJOINDRE LA LISTE D'ATTENTE</h4>

                    {/* Formation & Group */}
                    <div className="formation-details mb-4 p-4 border rounded bg-light">
                      {formationDetails && (
                        <>
                          <h5>Détails de la formation</h5>
                          <p>{formationDetails.nom_formation}</p>
                          {formationDetails.description_formation && (
                            <p className="text-muted small">
                              {formationDetails.description_formation}
                            </p>
                          )}
                        </>
                      )}
                      {groupDetails && (
                        <div className="d-flex align-items-center gap-3">
                          <Users className="text-primary" size={20} /> Groupe{" "}
                          {groupDetails.ID_Groupe}
                          <Clock
                            className="text-primary"
                            size={20}
                          /> Capacité: {groupDetails.Effectif_Actuel}/
                          {groupDetails.Capacite}
                        </div>
                      )}
                    </div>

                    <p className="text-warning mb-4">
                      <i className="fas fa-exclamation-triangle me-2"></i> Ce
                      groupe est complet. Rejoignez la liste d'attente.
                    </p>

                    {/* Form */}
                    {submitError && (
                      <div className="alert alert-warning mb-4 d-flex justify-content-between align-items-center">
                        <small>
                          <i className="fas fa-exclamation-triangle me-2"></i>
                          {submitError}
                        </small>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleSubmitRetry}
                        >
                          Réessayer
                        </button>
                      </div>
                    )}

                    {alert.message && !submitError && (
                      <div
                        className={`alert ${
                          alert.type === "success"
                            ? "alert-success"
                            : "alert-danger"
                        }`}
                      >
                        {alert.message}
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <div className="row gutter-20">
                        <div className="col-md-6">
                          <input
                            type="text"
                            name="nom_etudiant"
                            value={formData.nom_etudiant}
                            onChange={handleInputChange}
                            placeholder="Nom *"
                            required
                            disabled={submitting}
                            className="form-control mb-2"
                          />
                        </div>
                        <div className="col-md-6">
                          <input
                            type="text"
                            name="prenom_etudiant"
                            value={formData.prenom_etudiant}
                            onChange={handleInputChange}
                            placeholder="Prénom *"
                            required
                            disabled={submitting}
                            className="form-control mb-2"
                          />
                        </div>
                      </div>
                      <input
                        type="email"
                        name="email_etudiant"
                        value={formData.email_etudiant}
                        onChange={handleInputChange}
                        placeholder="Email *"
                        required
                        disabled={submitting}
                        className="form-control mb-2"
                      />
                      <input
                        type="tel"
                        name="numero_tel_etudiant"
                        value={formData.numero_tel_etudiant}
                        onChange={handleInputChange}
                        placeholder="Téléphone *"
                        required
                        disabled={submitting}
                        className="form-control mb-2"
                      />
                      <textarea
                        name="commentaire"
                        value={formData.commentaire}
                        onChange={handleInputChange}
                        placeholder="Commentaire (optionnel)"
                        rows={3}
                        className="form-control mb-3"
                        disabled={submitting}
                      ></textarea>

                      <button
                        type="submit"
                        className="btn btn-warning w-100"
                        disabled={submitting}
                      >
                        {" "}
                        {submitting
                          ? "Envoi..."
                          : "Rejoindre la liste d'attente"}{" "}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </main>
  );
};

export default Reservations;
